import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DmsUser } from '../models/dms-types';
import { SharedService } from '../../core/services/shared.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { stringConstant } from '../../core/services/stringConstants';
import { FirmContextService } from '../../firm-workspace/shared/services/firm-context.service';

@Injectable({ providedIn: 'root' })
export class DmsAuthService {
  private readonly user = signal<DmsUser | null>(null);

  private readonly hasTokenSignal = toSignal(
    this.sharedService.getUserLoginStatus(),
    { initialValue: !!this.sharedService.getLocalStorage(stringConstant.token) },
  );

  readonly isAuthenticated = computed(() => !!this.hasTokenSignal());
  readonly currentUser = this.user.asReadonly();

  constructor(
    private readonly router: Router,
    private readonly sharedService: SharedService,
    private readonly authenticationService: AuthenticationService,
    private readonly firmContext: FirmContextService,
  ) {}

  /**
   * Populates the DMS user from the active firm context. The firm role is the
   * single source of truth for DMS capabilities (no separate DMS role/API call).
   */
  fetchUser(): void {
    const firmRole = this.firmContext.activeFirm?.myRole;
    const token = this.sharedService.getLocalStorage(stringConstant.token);
    if (!token) { this.user.set(null); return; }

    // Populate from SharedService user info (already in memory)
    const email = this.sharedService.getLocalStorage('user_email') ?? '';
    this.user.set({
      id: '',
      username: email.split('@')[0] ?? '',
      email,
      first_name: '',
      last_name: '',
      firmRole: firmRole ?? undefined,
    });
  }

  logout(): void {
    this.user.set(null);
    this.authenticationService.auth0Logout(false);
    this.router.navigate(['/']);
  }

  updateUser(user: DmsUser): void {
    this.user.set(user);
  }
}
